import { AbstractBuildstep } from "@project/emerge";
import type {
  AbstractPartDefinition,
  DragConstraint,
  DragDiagonalSnap,
  DragUpdateOptions,
  DragUpdateResult,
  SnapAnchor,
  SupplyPartDefinition,
  SurfaceDragArea,
} from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import filter from "lodash/filter";
import * as THREE from "three";
import { AddableType } from "../enum/AddableType";
import { Alignment } from "../enum/Alignment";
import { Covers } from "../enum/Covers";
import { LTProducts } from "../enum/LTProducts";
import { OrientationOnRect } from "../enum/OrientationOnRect";
import { LTPartType } from "../enum/PartType";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { AddableGridInfos } from "../interfaces/AddableGridInfos";
import type { DishDefinition } from "../interfaces/DishProperties";
import type { DishType } from "../interfaces/DishType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { FootType } from "../interfaces/FootType";
import type { HandleDefinition } from "../interfaces/HandleProperties";
import type { HandleType } from "../interfaces/HandleType";
import type { Position } from "../interfaces/Position";
import type { TrolleyHandleDefinition } from "../interfaces/TrolleyHandleProperties";
import type { TrolleyHandleType } from "../interfaces/TrolleyHandleType";
import type { AddableInfos } from "../structure/AddableInfos";
import { compileAddableGrid } from "../structure/AddableInfos";
import type { Rect } from "../structure/Rect";
import { DefaultGroupName } from "../structure/Rect";
import { RectPropertiesBySide } from "../structure/RectProperties";
import {
  rebuildAddablePositionFromMargins,
  snapAddableDragToMarginGrid,
} from "../utils/addableDragGrid";
import {
  type AddableMarginDragRanges,
  clampMarginToRange,
  computeAddableMarginRangesForDragPosition,
  computeAddableMarginRangesFromDragPart,
  intersectMarginRanges,
} from "../utils/addableMarginDragRange";
import { gridStepFromOptions } from "../utils/dragGrid";
import { unionSurfaceAreaBounds } from "../utils/unionSurfaceAreaBounds";

/**
 * Addable grid `relativeToBody`: false = full side / Case, true = corpus (or hood base) only.
 * UI may store values as boolean or string ("true"/"false"). Omitted → false (Case), matching legacy.
 */
export function parseAddableRelativeToBody(raw: unknown): boolean {
  return raw === true || raw === "true";
}

// --- Concrete Property Types (assuming FootProperties is similar) ---
// Need FootProperties definition if not already imported/available globally
// For now, creating a basic one based on HandleProperties
type FootDefinition = SupplyPartDefinition<LTPartType.Foot, FootType>;

// --- Output Union Type ---
type AddablePartProperties =
  | DishDefinition
  | HandleDefinition
  | TrolleyHandleDefinition
  | FootDefinition; // Add other types like Runner if needed later

/** A world-axis name; the in-plane axes of a side are two of these. */
type Axis3 = "x" | "y" | "z";

/**
 * One candidate panel of the side, as raw (unpadded) geometry. The drag
 * constraint pads `rect*` for the visual/projection bounds; `logical*` is the
 * positioning reference frame for the reverse margin math. All panels of a side
 * are emitted so an addable can be dragged from one panel to another.
 */
interface AddableDragArea {
  rectMin: THREE.Vector3;
  rectMax: THREE.Vector3;
  rectCenter: THREE.Vector3;
}

// --- Dependency Data Interfaces ---
interface BaseAddableStepDependency {
  partId: string;
  groupName: string;
  buildstepName: string;
  isSupplyPart: true;
  position: THREE.Vector3; // Final calculated position
  // Drag constraint data
  sourceOptionUuid: string;
  rectBoundsMin: THREE.Vector3;
  rectBoundsMax: THREE.Vector3;
  rectNormal: THREE.Vector3;
  rectCenter: THREE.Vector3;
  // Logical bounds for calculation (may differ from visual bounds)
  logicalRectBoundsMin: THREE.Vector3;
  logicalRectBoundsMax: THREE.Vector3;
  logicalRectCenter: THREE.Vector3;
  // All candidate panels of this side (incl. the resolved one). Drives the
  // multi-area drag constraint so the part can jump across the gap between
  // panels. Index 0 is the resolved/primary panel.
  areas: AddableDragArea[];
  // Alignment ("Ausrichtung"): true = relative to corpus (local drag-area
  // center), false = relative to the whole case (global case-side center).
  relativeToBody: boolean;
  // Positioning context
  horizontalCount: number;
  verticalCount: number;
  horizontalN: number;
  verticalN: number;
  horizontalAlignment: Alignment;
  verticalAlignment: Alignment;
  // Angle section padding (to prevent overlap)
  angleSectionDepth: number;
  // Point-symmetric mirror copy: forward negates the CENTER margin, so the
  // reverse drag calculation must negate it back to keep direction correct.
  mirrored: boolean;
  /** resolvePosition1d reference midpoints (matches margin → world placement). */
  horizontalResolveMid: number;
  verticalResolveMid: number;
}

interface AddableDishDependencyData extends BaseAddableStepDependency {
  partKind: "AddableDish";
  type: LTPartType.Dish;
  partSelection: DishType;
  orientation: number; // degrees
  side: Sides;
  flip: boolean;
}

interface AddableHandleDependencyData extends BaseAddableStepDependency {
  partKind: "AddableHandle";
  type: LTPartType.Handle;
  partSelection: HandleType;
  orientation: number; // degrees
  side: Sides;
}

interface AddableTrolleyHandleDependencyData extends BaseAddableStepDependency {
  partKind: "AddableTrolleyHandle";
  type: LTPartType.TrolleyHandle;
  partSelection: TrolleyHandleType;
  orientation: number; // degrees
  side: Sides;
}

interface AddableFootDependencyData extends BaseAddableStepDependency {
  partKind: "AddableFoot";
  type: LTPartType.Foot;
  partSelection: FootType;
  // Optional side/orientation if needed for Feet
  side?: Sides;
  orientation?: number;
}

type AddableStepDependencyData =
  | AddableDishDependencyData
  | AddableHandleDependencyData
  | AddableTrolleyHandleDependencyData
  | AddableFootDependencyData;

// --- Helper ---
function getCut(
  orientation: number,
  orientationOnRect: OrientationOnRect,
  cutX?: number,
  cutY?: number,
): number | undefined {
  const a = orientation % 180 !== 0;
  const b = orientationOnRect === OrientationOnRect.HORIZONTAL;

  if (a !== b) {
    return cutX ? cutX / 2 : undefined;
  } else {
    return cutY ? cutY / 2 : undefined;
  }
}

// Map of addable types to part types (defined outside class)
const addableType2PartType: Record<AddableType, LTPartType | null> = {
  [AddableType.Dish]: LTPartType.Dish,
  [AddableType.Foot]: LTPartType.Foot,
  [AddableType.Handle]: LTPartType.Handle,
  [AddableType.Runner]: LTPartType.Roller, // Map Runner to Roller part type if correct? Double check this mapping.
  [AddableType.TrolleyHandle]: LTPartType.TrolleyHandle,
};

// --- Refactored Build Step ---
export class AddableBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AddablePartProperties,
  AddableStepDependencyData
> {
  constructor() {
    super("AddableBuildStep");
  }

  findRectsForSide(blueprint: FlightCaseBlueprint, side: Sides): Rect[] {
    return blueprint.rects.filter((rect) => rect.side === side);
  }

  /**
   * Legacy AddableBuilder: Relativ zum Korpus → only rects in the corpus group (or hood base);
   * Relativ zum Case → all rects on that side (covers + corpus), so positioning uses full-case edges.
   */
  findRectsForAddable(
    blueprint: FlightCaseBlueprint,
    side: Sides,
    relativeToBody: boolean,
    productType: string,
  ): Rect[] {
    const onSide = blueprint.rects.filter((rect) => rect.side === side);
    if (!relativeToBody) {
      return onSide;
    }
    const isHood =
      productType === LTProducts.Haube ||
      productType === LTProducts.KeyboardCase;
    const targetGroup = isHood ? Covers.BASE : DefaultGroupName;
    return onSide.filter((rect) => rect.group === targetGroup);
  }

  /**
   * Resolve the in-plane placement of an addable.
   *
   * - Case (and single-panel sides): the legacy first-rect-that-fits search over
   *   the reference rects. With `overFullcase` all side rects share the full-case
   *   frame, so the containing panel is selected and the part's group reflects it.
   * - Korpus on a split side: the margin is anchored at the corpus (primary
   *   reference) panel, but the part may be dragged across the gap onto a
   *   coplanar neighbour panel. Each panel has its *own* center under Korpus, so
   *   the legacy search (largest panel always wins, hard reject otherwise) cannot
   *   place beyond the corpus. We instead resolve against the corpus frame
   *   without the bounds reject; all side panels are coplanar so the surface
   *   position is identical regardless of which panel physically contains it.
   */
  private resolveAddablePlacement(
    referenceRects: Rect[],
    sideRects: Rect[],
    positions: { horizontalPosition: Position; verticalPosition: Position },
    orientation: number,
    cutX: number | undefined,
    cutY: number | undefined,
    relativeToBase: boolean,
  ): { rect: Rect; x: number; y: number } | null {
    const { horizontalPosition, verticalPosition } = positions;
    const standard = this.resolvePositionOnRects(
      referenceRects,
      horizontalPosition,
      verticalPosition,
      orientation,
      cutX,
      cutY,
      relativeToBase,
    );

    // Korpus + split side: keep the corpus anchor but allow the resolved point
    // to fall outside the corpus rect (onto a neighbour panel of the side).
    const crossPanelKorpus = relativeToBase && sideRects.length > 1;
    if (standard || !crossPanelKorpus) {
      return standard;
    }

    const anchor = referenceRects[0];
    if (!anchor) {
      return null;
    }
    const x = anchor.resolvePosition1d(
      horizontalPosition,
      OrientationOnRect.HORIZONTAL,
      getCut(orientation, OrientationOnRect.HORIZONTAL, cutX, cutY),
      !relativeToBase,
      false,
    );
    const y = anchor.resolvePosition1d(
      verticalPosition,
      OrientationOnRect.VERTICAL,
      getCut(orientation, OrientationOnRect.VERTICAL, cutX, cutY),
      !relativeToBase,
      false,
    );
    if (x === null || y === null) {
      return null;
    }
    return { rect: anchor, x, y };
  }

  // Helper: resolve position on rects
  private resolvePositionOnRects(
    rects: Rect[],
    horizontalPosition: Position,
    verticalPosition: Position,
    orientation: number,
    cutX?: number,
    cutY?: number,
    relativeToBase?: boolean,
  ): { rect: Rect; x: number; y: number } | null {
    for (const rect of rects) {
      const testX = rect.resolvePosition1d(
        horizontalPosition,
        OrientationOnRect.HORIZONTAL,
        getCut(orientation, OrientationOnRect.HORIZONTAL, cutX, cutY),
        !relativeToBase,
      );
      const testY = rect.resolvePosition1d(
        verticalPosition,
        OrientationOnRect.VERTICAL,
        getCut(orientation, OrientationOnRect.VERTICAL, cutX, cutY),
        !relativeToBase,
      );

      if (testX !== null && testY !== null) {
        return { rect, x: testX, y: testY };
      }
    }
    return null;
  }

  /**
   * Old `AddableBuilder`: `horizontalDir*x + verticalDir*y`, `position[normal] = outerCenter[normal]`.
   */
  private static composeAddablePositionVectorDirs(
    rect: Rect,
    resolvedHorizontal: number,
    resolvedVertical: number,
  ): THREE.Vector3 {
    const { horizontalDir, verticalDir, normal } = rect.properties;
    const position = new THREE.Vector3();
    position.add(horizontalDir.clone().multiplyScalar(resolvedHorizontal));
    position.add(verticalDir.clone().multiplyScalar(resolvedVertical));
    position[normal] = rect.outerCenter[normal];
    return position;
  }

  private calculatePosition(rect: Rect, x: number, y: number): THREE.Vector3 {
    return AddableBuildStep.composeAddablePositionVectorDirs(rect, x, y);
  }

  // Helper: create dependency for specific addable type
  private createAddableDependency(
    addableType: AddableType,
    partType: LTPartType,
    partSelection: DishType | HandleType | TrolleyHandleType | FootType,
    orientation: number,
    side: Sides,
    baseDep: BaseAddableStepDependency,
  ): AddableStepDependencyData | null {
    switch (addableType) {
      case AddableType.Dish: {
        let flip = false;
        const maybeFlip = (partSelection as { flip?: boolean }).flip;
        if (typeof maybeFlip === "boolean") flip = maybeFlip;
        return {
          ...baseDep,
          partKind: "AddableDish",
          type: partType as LTPartType.Dish,
          partSelection: partSelection as DishType,
          orientation,
          side,
          flip,
        };
      }
      case AddableType.Handle:
        return {
          ...baseDep,
          partKind: "AddableHandle",
          type: partType as LTPartType.Handle,
          partSelection: partSelection as HandleType,
          orientation,
          side,
        };
      case AddableType.TrolleyHandle:
        return {
          ...baseDep,
          partKind: "AddableTrolleyHandle",
          type: partType as LTPartType.TrolleyHandle,
          partSelection: partSelection as TrolleyHandleType,
          orientation,
          side,
        };
      case AddableType.Foot:
        return {
          ...baseDep,
          partKind: "AddableFoot",
          type: partType as LTPartType.Foot,
          partSelection: partSelection as FootType,
          side: side,
          orientation: orientation,
        };
      case AddableType.Runner:
        console.warn(
          "AddableType.Runner encountered, needs specific handling if mapped to Roller.",
        );
        return null;
      default:
        return null;
    }
  }

  // Helper: compute rect bounds for drag constraints
  private computeRectBounds(rect: Rect): {
    min: THREE.Vector3;
    max: THREE.Vector3;
    center: THREE.Vector3;
  } {
    // Get the inner corners of the rect (actual panel surface, not outer edge)
    const corners = rect.corners.map((c) => c.innerPos);
    const min = new THREE.Vector3(
      Math.min(...corners.map((c) => c.x)),
      Math.min(...corners.map((c) => c.y)),
      Math.min(...corners.map((c) => c.z)),
    );
    const max = new THREE.Vector3(
      Math.max(...corners.map((c) => c.x)),
      Math.max(...corners.map((c) => c.y)),
      Math.max(...corners.map((c) => c.z)),
    );
    return { min, max, center: rect.innerCenter };
  }

  // Helper: compute logical rect bounds for positioning calculation
  private computeLogicalRectBounds(
    rect: Rect,
    overFullcase: boolean,
  ): {
    min: THREE.Vector3;
    max: THREE.Vector3;
    center: THREE.Vector3;
  } {
    const points: THREE.Vector3[] = [];

    // Rect edges define the boundary
    // Use edges directly to match resolvePosition1d logic source
    for (const edge of rect.edges) {
      if (!edge) continue;
      if (overFullcase) {
        points.push(edge.start.originalOuterPos);
        points.push(edge.end.originalOuterPos);
      } else {
        points.push(edge.start.outerPos);
        points.push(edge.end.outerPos);
      }
    }

    // Fallback if edges missing
    if (points.length === 0) {
      return this.computeRectBounds(rect);
    }

    const min = new THREE.Vector3(
      Math.min(...points.map((p) => p.x)),
      Math.min(...points.map((p) => p.y)),
      Math.min(...points.map((p) => p.z)),
    );
    const max = new THREE.Vector3(
      Math.max(...points.map((p) => p.x)),
      Math.max(...points.map((p) => p.y)),
      Math.max(...points.map((p) => p.z)),
    );
    const center = min.clone().add(max).multiplyScalar(0.5);

    return { min, max, center };
  }

  // Helper: process single addable
  private processAddable(
    addable: AddableGridInfos,
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    /** Index among `filter(variableOptions, addableType)` — legacy rows can share `uuid`. */
    addableSlotIndex: number,
    angleSectionDepth: number,
  ): AddableStepDependencyData[] {
    const dependencies: AddableStepDependencyData[] = [];
    const addableType = addable.addableType;
    const partType = addableType2PartType[addableType];

    if (!partType) {
      console.warn(
        `AddableBuildStep: Unsupported addableType ${addableType}. Skipping.`,
      );
      return dependencies;
    }

    const addablePositions = compileAddableGrid(addable);
    const relativeToBase = parseAddableRelativeToBody(addable.relativeToBody);

    for (const gridElement of addablePositions) {
      const {
        partSelection,
        horizontalPosition,
        verticalPosition,
        orientation = 0,
        side,
        sourceOptionUuid,
        gridIndexH,
        gridIndexV,
      } = gridElement;

      if (!partSelection) continue;

      const { cutX, cutY } =
        (partSelection as { cutX?: number; cutY?: number }) ?? {};
      // Reference rects drive the primary placement frame (Korpus → corpus
      // group; Case → whole side). Side rects are every panel of the side and
      // become the drag areas, so the part can be dragged across the gap onto a
      // coplanar neighbour panel regardless of the alignment mode.
      const referenceRects = this.findRectsForAddable(
        blueprint,
        side,
        relativeToBase,
        settings.type,
      );
      if (referenceRects.length === 0) {
        console.warn(
          `AddableBuildStep: Could not find any rect for side ${side}. Skipping addable.`,
        );
        continue;
      }
      const sideRects = this.findRectsForSide(blueprint, side);

      const resolved = this.resolveAddablePlacement(
        referenceRects,
        sideRects,
        { horizontalPosition, verticalPosition },
        orientation,
        cutX,
        cutY,
        relativeToBase,
      );

      if (!resolved) {
        console.warn(
          `AddableBuildStep: Could not resolve 1D position for addable on side ${side} for any rect. Skipping.`,
        );
        continue;
      }

      const { rect, x, y } = resolved;

      const position = this.calculatePosition(rect, x, y);
      // Stable ID: slot disambiguates duplicate option uuids (e.g. two HANDLES rows, order 23629).
      const partId = `addable_${sourceOptionUuid}_s${addableSlotIndex}_${side}_h${gridIndexH}_v${gridIndexV}`;

      const baseDep = this.buildBaseDep(
        rect,
        gridElement,
        addable,
        relativeToBase,
        angleSectionDepth,
        { partId, position },
        sideRects,
      );

      const dep = this.createAddableDependency(
        addableType,
        partType,
        partSelection as DishType | HandleType | TrolleyHandleType | FootType,
        orientation,
        side,
        baseDep,
      );

      if (dep) {
        dependencies.push(dep);
      }
    }

    return dependencies;
  }

  // Helper: assemble the shared base dependency (drag bounds + positioning
  // context) for one grid element on a given rect. Shared by the part build and
  // by the geometry-only margin-range probe, so both derive identical bounds.
  private buildBaseDep(
    rect: Rect,
    gridElement: AddableInfos,
    addable: AddableGridInfos,
    relativeToBase: boolean,
    angleSectionDepth: number,
    placement: { partId: string; position: THREE.Vector3 },
    /** All candidate panels of the side; the resolved `rect` should be first. */
    areaRects: Rect[],
  ): BaseAddableStepDependency {
    const { partId, position } = placement;
    const {
      horizontalPosition,
      verticalPosition,
      side,
      sourceOptionUuid,
      mirrored,
    } = gridElement;

    const rectBounds = this.computeRectBounds(rect);
    // Logical bounds match the overFullcase logic in resolvePosition1d.
    const overFullcase = !relativeToBase;
    const logicalBounds = this.computeLogicalRectBounds(rect, overFullcase);

    // One area per candidate panel of the side. The resolved rect is kept first
    // so index 0 stays the "primary" area consistent with the single-field data.
    const orderedRects = [rect, ...areaRects.filter((r) => r !== rect)];
    const areas: AddableDragArea[] = orderedRects.map((r) => {
      const rb = this.computeRectBounds(r);
      return {
        rectMin: rb.min,
        rectMax: rb.max,
        rectCenter: rb.center,
      };
    });

    const rectProps = RectPropertiesBySide[side];
    const rectNormal = rectProps?.normalDir.clone() ?? new THREE.Vector3();

    return {
      partId,
      groupName: rect.group,
      buildstepName: this.buildstepName,
      isSupplyPart: true as const,
      position: position.clone(),
      // Drag constraint data
      sourceOptionUuid,
      rectBoundsMin: rectBounds.min,
      rectBoundsMax: rectBounds.max,
      rectNormal,
      rectCenter: rectBounds.center,
      // Logical bounds
      logicalRectBoundsMin: logicalBounds.min,
      logicalRectBoundsMax: logicalBounds.max,
      logicalRectCenter: logicalBounds.center,
      areas,
      relativeToBody: relativeToBase,
      // Positioning context
      horizontalCount: addable.horizontalCount ?? 1,
      verticalCount: addable.verticalCount ?? 1,
      // Ensure n values are valid numbers (fallback to 0.5 for center if NaN)
      horizontalN: Number.isNaN(horizontalPosition.n)
        ? 0.5
        : horizontalPosition.n,
      verticalN: Number.isNaN(verticalPosition.n) ? 0.5 : verticalPosition.n,
      horizontalAlignment: horizontalPosition.alignment,
      verticalAlignment: verticalPosition.alignment,
      // Angle section padding
      angleSectionDepth,
      mirrored,
      horizontalResolveMid: rect.resolveAxisMid(
        OrientationOnRect.HORIZONTAL,
        overFullcase,
      ),
      verticalResolveMid: rect.resolveAxisMid(
        OrientationOnRect.VERTICAL,
        overFullcase,
      ),
    };
  }

  /**
   * Compute the valid horizontal/vertical margin range for an addable option
   * straight from the current blueprint geometry — independent of whether the
   * part currently resolves to a position. This lets callers clamp a stored
   * "Abstand" back into the drag area even when the old margin pushed the part
   * fully off its rect (so no part was emitted to read a constraint from).
   *
   * Ranges are intersected across all grid cells so the clamped margin keeps
   * every cell of the option inside its area.
   */
  public computeMarginRangesForOption(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    addable: AddableGridInfos,
  ): AddableMarginDragRanges | null {
    const partType = addableType2PartType[addable.addableType];
    if (!partType) {
      return null;
    }

    const angleSectionDepth =
      settings.exteriorOptions.angleSections?.depth ?? 0;
    const relativeToBase = parseAddableRelativeToBody(addable.relativeToBody);
    const addablePositions = compileAddableGrid(addable);

    let combined: AddableMarginDragRanges | null = null;
    for (const gridElement of addablePositions) {
      const { partSelection, side, orientation = 0 } = gridElement;
      if (!partSelection) {
        continue;
      }
      const referenceRects = this.findRectsForAddable(
        blueprint,
        side,
        relativeToBase,
        settings.type,
      );
      // The range derives purely from the target rect's geometry; the first
      // candidate rect is the placement (anchor) target for this side.
      const rect = referenceRects[0];
      if (!rect) {
        continue;
      }
      // All side panels feed the union bounds so the clamp range spans the whole
      // side (matching the cross-panel drag area), not just the corpus panel.
      const sideRects = this.findRectsForSide(blueprint, side);

      const baseDep = this.buildBaseDep(
        rect,
        gridElement,
        addable,
        relativeToBase,
        angleSectionDepth,
        { partId: "margin-range-probe", position: new THREE.Vector3() },
        sideRects,
      );

      const { cutX, cutY } =
        (partSelection as { cutX?: number; cutY?: number }) ?? {};
      const constraint = this.buildSurfaceDragConstraint(
        baseDep,
        side,
        cutX,
        cutY,
        orientation,
      );
      const settingsContext = this.buildSettingsContext(baseDep, side);
      // Minimal stand-in: computeAddableMarginRangesFromDragPart only reads
      // dragConstraint + settingsContext from the part.
      const probePart = {
        dragConstraint: constraint,
        settingsContext,
      } as unknown as AbstractPartDefinition<string>;

      const range = computeAddableMarginRangesFromDragPart(probePart, addable);
      if (!range) {
        continue;
      }
      combined = combined ? intersectMarginRanges(combined, range) : range;
    }

    return combined;
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, AddableStepDependencyData> {
    const dependencies = new Map<string, AddableStepDependencyData>();

    // Get angle section depth for constraint padding
    const angleSectionDepth =
      settings.exteriorOptions.angleSections?.depth ?? 0;

    // Filter variable options to find addables
    const addables = filter(settings.variableOptions, "addableType");
    let addableSlotIndex = 0;

    for (const addable of addables) {
      switch (addable.variableOptionType) {
        case VariableOptionType.DISHES:
        case VariableOptionType.TROLLEY_HANDLES:
        case VariableOptionType.HANDLES:
        case VariableOptionType.FEET: {
          const deps = this.processAddable(
            addable,
            blueprint,
            settings,
            addableSlotIndex++,
            angleSectionDepth,
          );
          deps.forEach((dep: AddableStepDependencyData) => {
            dependencies.set(dep.partId, dep);
          });
          break;
        }
      }
    }

    return dependencies;
  }

  // Shrink a bounds box inward by the given in-plane paddings. Works regardless
  // of whether min/max are ordered along each axis (sides differ in direction).
  private static padSurfaceBounds(
    min: THREE.Vector3,
    max: THREE.Vector3,
    horizontalAxis: Axis3,
    verticalAxis: Axis3,
    horizontalPadding: number,
    verticalPadding: number,
  ): { min: THREE.Vector3; max: THREE.Vector3 } {
    const paddedMin = min.clone();
    const paddedMax = max.clone();

    if (paddedMin[horizontalAxis] < paddedMax[horizontalAxis]) {
      paddedMin[horizontalAxis] += horizontalPadding;
      paddedMax[horizontalAxis] -= horizontalPadding;
    } else {
      paddedMin[horizontalAxis] -= horizontalPadding;
      paddedMax[horizontalAxis] += horizontalPadding;
    }

    if (paddedMin[verticalAxis] < paddedMax[verticalAxis]) {
      paddedMin[verticalAxis] += verticalPadding;
      paddedMax[verticalAxis] -= verticalPadding;
    } else {
      paddedMin[verticalAxis] -= verticalPadding;
      paddedMax[verticalAxis] += verticalPadding;
    }

    return { min: paddedMin, max: paddedMax };
  }

  /**
   * 45° diagonal snap from this foot's quadrant corner toward the panel center
   * (equal horizontal + vertical margin), matching {@link RollerBuildStep}.
   */
  private static buildQuadrantDiagonalSnaps(
    side: Sides,
    paddedMin: THREE.Vector3,
    paddedMax: THREE.Vector3,
    horizontalN: number,
    verticalN: number,
    horizontalCount: number,
    verticalCount: number,
  ): DragDiagonalSnap[] | undefined {
    if (horizontalCount < 2 || verticalCount < 2) {
      return undefined;
    }

    const rectProps = RectPropertiesBySide[side];
    if (!rectProps) {
      return undefined;
    }

    const { horizontal, vertical, horizontalDir, verticalDir, normal } =
      rectProps;

    const hMin = Math.min(paddedMin[horizontal], paddedMax[horizontal]);
    const hMax = Math.max(paddedMin[horizontal], paddedMax[horizontal]);
    const vMin = Math.min(paddedMin[vertical], paddedMax[vertical]);
    const vMax = Math.max(paddedMin[vertical], paddedMax[vertical]);

    const halfH = (hMax - hMin) / 2;
    const halfV = (vMax - vMin) / 2;
    const diagonalLength = Math.min(halfH, halfV) * Math.SQRT2;
    if (diagonalLength <= 0) {
      return undefined;
    }

    const signH = horizontalN < 0.5 ? -1 : 1;
    const signV = verticalN < 0.5 ? -1 : 1;

    const corner = new THREE.Vector3();
    corner[normal] = (paddedMin[normal] + paddedMax[normal]) / 2;
    corner[horizontal] = signH < 0 ? hMin : hMax;
    corner[vertical] = signV < 0 ? vMin : vMax;

    const direction = horizontalDir
      .clone()
      .multiplyScalar(-signH)
      .add(verticalDir.clone().multiplyScalar(-signV))
      .normalize();

    return [
      {
        origin: [corner.x, corner.y, corner.z],
        direction: [direction.x, direction.y, direction.z],
        length: diagonalLength,
        label: "Diagonal",
      },
    ];
  }

  // Helper: build drag constraint for surface movement
  private buildSurfaceDragConstraint(
    dependencies: BaseAddableStepDependency,
    side: Sides,
    partCutX?: number,
    partCutY?: number,
    orientation?: number,
    /** Feet in a 2×2 (or larger) corner grid: 45° diagonal snap like rollers. */
    enableDiagonalSnaps = false,
  ): DragConstraint {
    const {
      rectBoundsMin,
      rectBoundsMax,
      rectNormal,
      rectCenter,
      logicalRectCenter,
      relativeToBody,
      sourceOptionUuid,
      angleSectionDepth,
      areas: areaInfos,
      horizontalCount,
      verticalCount,
      horizontalN,
      verticalN,
    } = dependencies;

    // Calculate total padding: angle section depth + half of part's own size
    // Part size depends on orientation (cutX/cutY may swap based on rotation)
    const orientedCutX =
      orientation && orientation % 180 !== 0
        ? (partCutY ?? 0)
        : (partCutX ?? 0);
    const orientedCutY =
      orientation && orientation % 180 !== 0
        ? (partCutX ?? 0)
        : (partCutY ?? 0);

    const horizontalPadding = angleSectionDepth + orientedCutX / 2;
    const verticalPadding = angleSectionDepth + orientedCutY / 2;

    const rectProps = RectPropertiesBySide[side];
    // Get which axis is horizontal/vertical for this side (e.g., "x", "y", "z")
    const horizontalAxis = rectProps?.horizontal ?? "x";
    const verticalAxis = rectProps?.vertical ?? "y";

    // Pad every candidate panel and expose them as separate drag areas, so the
    // part can be dragged from one panel to another across the gap. Fall back to
    // the primary (resolved) panel when no area list is present.
    const sourceAreas: AddableDragArea[] =
      areaInfos.length > 0
        ? areaInfos
        : [
            {
              rectMin: rectBoundsMin,
              rectMax: rectBoundsMax,
              rectCenter,
            },
          ];

    const areas: SurfaceDragArea[] = sourceAreas.map((area) => {
      const padded = AddableBuildStep.padSurfaceBounds(
        area.rectMin,
        area.rectMax,
        horizontalAxis,
        verticalAxis,
        horizontalPadding,
        verticalPadding,
      );
      return {
        bounds: {
          min: [padded.min.x, padded.min.y, padded.min.z],
          max: [padded.max.x, padded.max.y, padded.max.z],
        },
        center: [area.rectCenter.x, area.rectCenter.y, area.rectCenter.z],
      };
    });

    // Union bounding box of all padded areas: the overall movement extent used
    // for visualization fallback and by the margin-range util (whole-side
    // envelope, so the auto-correct clamp does not pull a part that validly sits
    // on another panel back toward the primary panel).
    const unionBounds = unionSurfaceAreaBounds(areas);
    const paddedMin = new THREE.Vector3(...unionBounds.min);
    const paddedMax = new THREE.Vector3(...unionBounds.max);

    // Center snap line depends on the addable's alignment ("Ausrichtung"):
    // - Korpus (relativeToBody): snap to the local center of the current drag
    //   area (the panel's inner center) — the part centers within its own rect.
    // - Case (!relativeToBody): snap to the global center of the whole case
    //   side, so the part aligns to the side's middle even when its drag rect is
    //   offset. Take the in-plane coords from the full-side logical center and
    //   keep the surface-normal coord from rectCenter so the guide line stays on
    //   the face. This matches the reverse margin math (also logicalRectCenter),
    //   so snapping here yields a centered (margin 0) result.
    const snapCenter = rectCenter.clone();
    if (!relativeToBody) {
      snapCenter[horizontalAxis] = logicalRectCenter[horizontalAxis];
      snapCenter[verticalAxis] = logicalRectCenter[verticalAxis];
    }
    const snapAnchors: SnapAnchor[] = [
      {
        position: [snapCenter.x, snapCenter.y, snapCenter.z],
        label: "Center",
      },
    ];

    const primaryPaddedMin = new THREE.Vector3(
      areas[0]?.bounds.min[0] ?? paddedMin.x,
      areas[0]?.bounds.min[1] ?? paddedMin.y,
      areas[0]?.bounds.min[2] ?? paddedMin.z,
    );
    const primaryPaddedMax = new THREE.Vector3(
      areas[0]?.bounds.max[0] ?? paddedMax.x,
      areas[0]?.bounds.max[1] ?? paddedMax.y,
      areas[0]?.bounds.max[2] ?? paddedMax.z,
    );
    const diagonalSnaps = enableDiagonalSnaps
      ? AddableBuildStep.buildQuadrantDiagonalSnaps(
          side,
          primaryPaddedMin,
          primaryPaddedMax,
          horizontalN,
          verticalN,
          horizontalCount,
          verticalCount,
        )
      : undefined;

    return {
      type: "surface",
      planeNormal: [rectNormal.x, rectNormal.y, rectNormal.z],
      bounds: {
        min: [paddedMin.x, paddedMin.y, paddedMin.z],
        max: [paddedMax.x, paddedMax.y, paddedMax.z],
      },
      // Only expose multiple areas (>1) — a single area equals `bounds`, so the
      // legacy single-bounds projection path stays in effect for unsplit sides.
      ...(areas.length > 1 ? { areas } : {}),
      // Korpus snaps to each panel's own center (per-area guide lines); Case
      // snaps to the single global side center (one guide line across the side).
      perAreaCenterSnap: relativeToBody,
      snapAnchors,
      ...(diagonalSnaps ? { diagonalSnaps } : {}),
      sourceOptionUuid,
      // Addables sit exactly at the dragged surface point, so the optimistic
      // local position round-trips 1:1 with the rebuild — safe to follow the
      // cursor at frame rate.
      optimistic: true,
    };
  }

  // Helper: build settings context for drag updates
  private buildSettingsContext(
    dependencies: BaseAddableStepDependency,
    side: Sides,
  ): Record<string, unknown> {
    const {
      rectBoundsMin,
      rectBoundsMax,
      rectNormal,
      rectCenter,
      logicalRectBoundsMin,
      logicalRectBoundsMax,
      logicalRectCenter,
      horizontalCount,
      verticalCount,
      horizontalN,
      verticalN,
      horizontalAlignment,
      verticalAlignment,
      angleSectionDepth,
      mirrored,
      horizontalResolveMid,
      verticalResolveMid,
    } = dependencies;
    return {
      side,
      rectBoundsMin: [rectBoundsMin.x, rectBoundsMin.y, rectBoundsMin.z],
      rectBoundsMax: [rectBoundsMax.x, rectBoundsMax.y, rectBoundsMax.z],
      rectNormal: [rectNormal.x, rectNormal.y, rectNormal.z],
      rectCenter: [rectCenter.x, rectCenter.y, rectCenter.z],
      logicalRectBoundsMin: [
        logicalRectBoundsMin.x,
        logicalRectBoundsMin.y,
        logicalRectBoundsMin.z,
      ],
      logicalRectBoundsMax: [
        logicalRectBoundsMax.x,
        logicalRectBoundsMax.y,
        logicalRectBoundsMax.z,
      ],
      logicalRectCenter: [
        logicalRectCenter.x,
        logicalRectCenter.y,
        logicalRectCenter.z,
      ],
      horizontalCount,
      verticalCount,
      horizontalN,
      verticalN,
      horizontalAlignment,
      verticalAlignment,
      angleSectionDepth,
      mirrored,
      horizontalResolveMid,
      verticalResolveMid,
    };
  }

  public createPart(
    partId: string,
    dependencies: AddableStepDependencyData,
  ): AddablePartProperties {
    // Common logic: calculate rotation if applicable
    let finalRotation: THREE.Euler | undefined;
    if (
      "side" in dependencies &&
      typeof dependencies.side === "number" &&
      "orientation" in dependencies &&
      typeof dependencies.orientation === "number"
    ) {
      finalRotation = this.rotateOntoSide(
        dependencies.side,
        dependencies.orientation,
      );
    } else {
      // Default rotation for parts without explicit side/orientation (e.g., Foot)
      finalRotation = new THREE.Euler(0, 0, 0);
    }

    // Extract part size from partSelection for padding calculation
    const partSelection = dependencies.partSelection as {
      cutX?: number;
      cutY?: number;
    };
    const partCutX = partSelection?.cutX;
    const partCutY = partSelection?.cutY;
    const orientation =
      "orientation" in dependencies ? dependencies.orientation : 0;

    // Return specific type based on partKind
    switch (dependencies.partKind) {
      case "AddableDish":
        return {
          // Base SupplyPart props from dependencies
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation,
          side: dependencies.side,
          flip: dependencies.flip,
          // Drag constraint for surface movement
          dragConstraint: this.buildSurfaceDragConstraint(
            dependencies,
            dependencies.side,
            partCutX,
            partCutY,
            orientation,
          ),
          settingsContext: this.buildSettingsContext(
            dependencies,
            dependencies.side,
          ),
        } as DishDefinition;
      case "AddableHandle":
        return {
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation,
          side: dependencies.side,
          // Drag constraint for surface movement
          dragConstraint: this.buildSurfaceDragConstraint(
            dependencies,
            dependencies.side,
            partCutX,
            partCutY,
            orientation,
          ),
          settingsContext: this.buildSettingsContext(
            dependencies,
            dependencies.side,
          ),
        } as HandleDefinition;
      case "AddableTrolleyHandle":
        return {
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation,
          side: dependencies.side,
          // Drag constraint for surface movement
          dragConstraint: this.buildSurfaceDragConstraint(
            dependencies,
            dependencies.side,
            partCutX,
            partCutY,
            orientation,
          ),
          settingsContext: this.buildSettingsContext(
            dependencies,
            dependencies.side,
          ),
        } as TrolleyHandleDefinition;
      case "AddableFoot":
        return {
          id: partId,
          type: dependencies.type,
          group: dependencies.groupName,
          buildstepName: dependencies.buildstepName,
          isSupplyPart: dependencies.isSupplyPart,
          position: dependencies.position.clone(),
          partSelection: dependencies.partSelection,
          // Calculated/specific
          rotation: finalRotation,
          orientation: dependencies.orientation ?? 0,
          side: dependencies.side,
          // Drag constraint for surface movement (if side is defined)
          ...(dependencies.side !== undefined
            ? {
                dragConstraint: this.buildSurfaceDragConstraint(
                  dependencies,
                  dependencies.side,
                  partCutX,
                  partCutY,
                  orientation,
                  dependencies.horizontalCount >= 2 &&
                    dependencies.verticalCount >= 2,
                ),
                settingsContext: this.buildSettingsContext(
                  dependencies,
                  dependencies.side,
                ),
              }
            : {}),
        } as FootDefinition;
      default: {
        const exhaustiveCheck: never = dependencies;
        throw new Error(`Unknown addable part kind: ${exhaustiveCheck}`);
      }
    }
  }

  /**
   * Convert a drag position change back to settings updates.
   * For addables on surfaces, this updates the horizontal/vertical margins.
   * Returns both the settings update and display values for UI feedback.
   */
  public updateSettingsFromDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    if (!constraint.sourceOptionUuid || !settingsContext) {
      return null;
    }

    // Find the variable option by UUID
    const optionIndex = currentSettings.variableOptions.findIndex(
      (opt) => opt.uuid === constraint.sourceOptionUuid,
    );

    if (optionIndex === -1) {
      console.warn(
        `Could not find variableOption with UUID ${constraint.sourceOptionUuid}`,
      );
      return null;
    }

    const option = currentSettings.variableOptions[
      optionIndex
    ] as unknown as AddableGridInfos;

    const gridStepMm = gridStepFromOptions(options);
    const probePart = {
      dragConstraint: constraint,
      settingsContext,
    } as AbstractPartDefinition<string>;
    const marginRanges = computeAddableMarginRangesForDragPosition(
      probePart,
      option,
      newPosition,
    );
    const gridded = snapAddableDragToMarginGrid(
      newPosition,
      settingsContext,
      option,
      gridStepMm,
      marginRanges,
    );
    if (!gridded) {
      return null;
    }

    const horizontalMargin = marginRanges
      ? clampMarginToRange(gridded.horizontalMargin, marginRanges.horizontal)
      : gridded.horizontalMargin;
    const verticalMargin = marginRanges
      ? clampMarginToRange(gridded.verticalMargin, marginRanges.vertical)
      : gridded.verticalMargin;
    const snappedPosition =
      rebuildAddablePositionFromMargins(
        newPosition,
        settingsContext,
        option,
        horizontalMargin,
        verticalMargin,
      ) ?? gridded.snappedPosition;

    // Create updated variable options array
    const updatedVariableOptions = [...currentSettings.variableOptions];
    updatedVariableOptions[optionIndex] = {
      ...option,
      horizontalMargin,
      verticalMargin,
    };

    return {
      settingsUpdate: {
        variableOptions: updatedVariableOptions,
      },
      displayValues: {
        primary: {
          label: "H",
          value: horizontalMargin,
          unit: "mm",
        },
        secondary: {
          label: "V",
          value: verticalMargin,
          unit: "mm",
        },
      },
      snappedPosition: [
        snappedPosition.x,
        snappedPosition.y,
        snappedPosition.z,
      ],
    };
  }
}
